import { Tooltip, makeStyles } from '@material-ui/core';
import Avatar from '@material-ui/core/Avatar';
import React from 'react';
import styled from 'styled-components';
import { getColor } from 'common/constants/_colors';
import { GIT_PROVIDERS } from 'common/constants/constants';
import SyncedIndicator from 'components/SyncedIndicator/SyncedIndicator';

const sizes = {
  small: {
    syncedIconSize: 'x-small'
  },
  medium: {
    syncedIconSize: 'smaller'
  },
  large: {
    syncedIconSize: 'smaller'
  }
};

const defaultSize = '3rem';

const WhiteBorder = styled.div`
  position: relative;
  height: min-content;
  width: min-content;
  padding: 2px;
  border-radius: 50%;
  background: #fff;
  z-index: 0;
  cursor: ${({ isclickable }) => (isclickable ? 'pointer' : 'auto')};
  ${({ isGrayedOut }) => (isGrayedOut ? `filter: grayscale(100%)` : '')};
`;

const OpacityLayer = styled.div`
  ${({ size }) =>
    size
      ? `width: ${size}; height: ${size};`
      : `
    width: ${defaultSize};
    height: ${defaultSize};`}
  display: flex;
  overflow: hidden;
  font-size: 1.25rem;
  align-items: center;
  line-height: 1;
  border-radius: 50%;
  position: absolute !important;
  background-color: ${getColor('white')};
  z-index: 2;
  opacity: 0.5;
`;

const AVATAR_BADGE_TYPES = {
  orange: `background: ${getColor(
    'orangePeel'
  )}; border: 1px solid transparent; color: ${getColor('white')}`
};

export const SizedAvatar = styled(Avatar)`
  ${({ size }) =>
    size ? `width: ${size} !important; height: ${size} !important;` : ''}
`;

const AvatarBadge = styled.div`
  position: absolute;
  right: 0;
  bottom: 0;
  border-radius: 50%;
  font-weight: bold;
  display: flex;
  align-items: center;
  justify-content: center;
  ${({ type }) =>
    type && AVATAR_BADGE_TYPES[type]
      ? AVATAR_BADGE_TYPES[type]
      : `background: ${getColor('white')}; border: 1px solid ${getColor(
          'botticelli'
        )}; color: ${getColor('shuttleGray')};`}
  ${({ size }) =>
    `width: ${parseInt(size || defaultSize) / 2.3}rem;
    height: ${parseInt(size || defaultSize) / 2.3}rem;
    font-size: ${parseInt(size || defaultSize) / 3.5}rem;`}
`;

const StyledAvatar = ({
  avatar,
  idx,
  onClick,
  classes: defaultClasses,
  content,
  disableTooltip,
  changeCursor,
  size = 'medium',
  badge,
  isSynced = false
}) => {
  const { name, email, color, url, isGrayedOut } = avatar;
  const { badgeContent, badgeType } = badge || {};

  const classes = color
    ? makeStyles({
        colorDefault: {
          color: '#fff',
          backgroundColor: color
        }
      })()
    : defaultClasses;

  return (
    <Tooltip
      title={`${name || ''}${email ? ` (${email})` : ''}`}
      key={`t_${idx}`}
      disableHoverListener={disableTooltip}
    >
      <WhiteBorder
        style={{ marginLeft: idx > 0 ? `-1.25rem` : 0 }}
        isclickable={changeCursor || !!onClick}
        onClick={() => onClick && onClick(avatar)}
        isGrayedOut={isGrayedOut}
      >
        {isGrayedOut && <OpacityLayer size={size} />}
        <SizedAvatar
          {...(url ? { src: url } : {})}
          alt=''
          classes={classes}
          size={size}
        >
          {content}
        </SizedAvatar>
        {isSynced && (
          <AvatarBadge>
            <SyncedIndicator
              gitProviderId={GIT_PROVIDERS.GITHUB}
              size={sizes[size].syncedIconSize}
            />
          </AvatarBadge>
        )}
        {badge && badgeContent && (
          <AvatarBadge data-testid='avatar-badge' size={size} type={badgeType}>
            {badgeContent}
          </AvatarBadge>
        )}
      </WhiteBorder>
    </Tooltip>
  );
};

export default StyledAvatar;
